import React, { useEffect, useState, useRef } from 'react';
import SavedItemsComponent from './ui';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Loading from 'components/Login/Loading';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useNavigate, useLocation } from 'react-router';
import { useSaveItemsPageFunctions } from 'components/SavedItems/useSaveItemsPageFunctions';
import { useSavedItemsContext } from 'components/SavedItems/SavedItemsContext';

const SavedItems = () => {
  const { state, setState } = useSavedItemsContext();
  const {
    userSavedItemsFolders,
    currentFolderSavedItems,
    savedItems,
    isLoadingSavedItemsList,
    readyUserSavedItemsFolders,
  } = state;
  const [filtersResources, setFiltersResources] = useState([]);

  const { generalApiCall } = useGeneralApiCall();
  const { savedItemsSearchDescriptor, loadFolders, loadFolderItems } = useSaveItemsPageFunctions();

  const location = useLocation();
  const navigate = useNavigate();
  const folderParam = getUrlParam('folder-id');

  const initialActions = useRef(null);
  const sourceRef = useRef(new AbortController());

  initialActions.current = async (source) => {
    try {
      setState('isLoadingSavedItemsList', true);
      setState('isLoadingFolder', true);
      await loadFolders(source);
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    if (readyUserSavedItemsFolders) {
      initialActions.current(source);
    }
    return () => {
      source.abort('Saved items cancelled by the user');
    };
  }, [readyUserSavedItemsFolders]);

  const updateFolderForParameter = useRef(null);
  updateFolderForParameter.current = (source) => {
    try {
      let position = userSavedItemsFolders.findIndex((item) => item.id === parseInt(folderParam));
      if (position >= 0) {
        setState('currentFolderSavedItems', userSavedItemsFolders[position !== undefined ? position : 0] ?? {});
        setFiltersResources([]);
        loadFolderItems(parseInt(folderParam), false, source);
      }
      if (userSavedItemsFolders?.length === 0) {
        setState('isLoadingFolder', false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    //OP: wait for initialActions to finish before loading the folder
    if (!isLoadingSavedItemsList && readyUserSavedItemsFolders) {
      updateFolderForParameter.current(source);
    }
    return () => {
      source.abort('Saved items canceled by the user.');
    };
  }, [folderParam, userSavedItemsFolders, isLoadingSavedItemsList, readyUserSavedItemsFolders]);

  //CREATE A FOLDER
  const createEditAFolder = async (props) => {
    const { e, name, id } = props;
    let el = e.target;
    el.disabled = true;
    let pathname = `/api/saved-items/${id ? 'update' : 'create'}-saved-item-folder`;
    let method = 'post';
    let requestProperties = {
      name,
    };
    if (id) {
      requestProperties = { ...requestProperties, id };
    }
    let results = await generalApiCall({ pathname, method, needsAuthentication: true, requestProperties });
    if (results) {
      let newFolders = [...userSavedItemsFolders];
      if (id) {
        let position = newFolders.findIndex((item) => item.id === id);
        newFolders[position].name = name;
        setState('userSavedItemsFolders', newFolders);
      } else {
        newFolders = [...newFolders, results];
        setState('currentFolderSavedItems', results ?? {});
        setState('userSavedItemsFolders', newFolders);
        await loadFolderItems(results.id);
      }
      createNotification('success', `Folder ${id ? 'edited' : 'created'} successfully`);
      return true;
    }
    el.disabled = false;
    return false;
  };

  const changeFolder = (folder) => {
    setState('scrollPositionSavedItems', null);
    sourceRef.current.abort('Mark item canceled by the user');
    navigate(`${location.pathname}${savedItemsSearchDescriptor(folder.id)}`);
  };

  const removeFromFolder = async (e, id) => {
    let el = e.target;
    el.disabled = true;
    let pathname = `/api/saved-items/saved-item/${id}`;
    let method = 'delete';
    let results = await generalApiCall({ pathname, method, needsAuthentication: true, returnCompleteRequest: true });
    if (results.status === 200) {
      let newItems = savedItems.slice();
      let position = newItems.findIndex((item) => item.id === id);
      newItems.splice(position, 1);
      setState('savedItems', newItems);
      //update count number
      let newFolders = [...userSavedItemsFolders];
      let folderPosition = newFolders.findIndex((item) => item.id === currentFolderSavedItems.id);
      newFolders[folderPosition].itemCount -= 1;
      setState('userSavedItemsFolders', newFolders);
      createNotification('success', `Removed from ${currentFolderSavedItems.name}`);
    } else {
      el.disabled = false;
    }
  };

  const deleteAFolder = async (e, id) => {
    if (userSavedItemsFolders.length > 1) {
      let el = e.target;
      el.disabled = true;
      let pathname = `/api/saved-items/saved-item-folder/${id}`;
      let method = 'delete';
      let results = await generalApiCall({ pathname, method, needsAuthentication: true, returnCompleteRequest: true });
      if (results.status === 200) {
        let newFolders = [...userSavedItemsFolders];
        let position = newFolders.findIndex((item) => item.id === id);
        newFolders.splice(position, 1);
        setState('userSavedItemsFolders', newFolders);
        let newCurrentFolder = newFolders[newFolders.length - 1];
        setState('currentFolderSavedItems', newCurrentFolder);
        loadFolderItems(newCurrentFolder.id);
        createNotification('success', 'Folder deleted successfully');
        return true;
      } else {
        el.disabled = false;
      }
    } else {
      createNotification('danger', 'You must have at least 1 folder');
    }
  };

  return (
    <SavedItemsComponent
      folders={userSavedItemsFolders}
      createEditAFolder={createEditAFolder}
      currentFolder={currentFolderSavedItems}
      changeFolder={changeFolder}
      removeFromFolder={removeFromFolder}
      deleteAFolder={deleteAFolder}
      loadFolderItems={loadFolderItems}
      filtersResources={filtersResources}
      setFiltersResources={setFiltersResources}
    />
  );
};

export default withAuthenticationRequired(SavedItems, {
  onRedirecting: () => <Loading />,
});
